import React, { useEffect, useState } from 'react';

import moment from 'moment';
import PropTypes from 'prop-types';
import jwt_decode from 'jwt-decode';
import Cookies from 'universal-cookie';
import { setWithCredentials } from 'services/api';
import { useFusionAuth } from '@fusionauth/react-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { getAuthRedirectUrl, hasAuthCookie, clearStorageItems } from 'utils';
import { FusionAuthProvider } from '@fusionauth/react-sdk';
import { restoreUserAccess } from 'state/modules/auth/actions';
import { getAuthUrl } from 'utils/utils';

const AuthContext = React.createContext();

const FusionAuthWrapper = ({ children }) => {
  const dispatch = useDispatch();
  const { currentBastion } = useSelector((state) => state.auth);
  const { startLogout, initAutoRefresh, refreshToken } = useFusionAuth();

  const checkAndRefreshToken = async (clientId) => {
    try {
      const cookies = new Cookies();
      const idtToken = cookies.get('app.idt');

      if (idtToken) {
        const decodedToken = jwt_decode(idtToken);
        if (decodedToken?.aud && decodedToken.aud === clientId) {
          if (decodedToken?.exp && moment.unix(decodedToken.exp).isBefore(moment())) {
            await refreshToken();
            setWithCredentials(true);
            initAutoRefresh();
            dispatch(restoreUserAccess());
          } else {
            setWithCredentials(true);
          }
        }
      }
    } catch {
      startLogout();
    }
  };

  useEffect(() => {
    const clientId =
      currentBastion?.clients.find((item) => item.client_type === 'MCD')?.client_id || '';
    if (clientId) {
      checkAndRefreshToken(clientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return children;
};

const AuthContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { startLogout } = useFusionAuth();
  const [authConfig, setAuthConfig] = useState();
  const { currentBastion } = useSelector((state) => state.auth);

  useEffect(() => {
    if (currentBastion?.clients.length > 0) {
      const clientId =
        currentBastion.clients?.find((item) => item.client_type === 'MCD')?.client_id || '';

      if (!clientId) {
        dispatch(restoreUserAccess());
        return;
      }

      localStorage.setItem('client_id', clientId);

      setAuthConfig({
        clientId: clientId,
        redirectUri: getAuthRedirectUrl(),
        serverUrl: getAuthUrl() || '',
        shouldAutoFetchUserInfo: true,
        shouldAutoRefresh: true,
        onAutoRefreshFailure: () => {
          startLogout();
        },
        onRedirect: (state) => {
          console.log(`Redirect happened with state value: ${state}`);
        },
        scope: 'openid email profile offline_access',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBastion, dispatch]);

  useEffect(() => {
    if (hasAuthCookie()) {
      setWithCredentials(true);
      dispatch(restoreUserAccess());
    } else if (localStorage.getItem('access_token')) {
      dispatch(restoreUserAccess());
    } else {
      clearStorageItems();
      dispatch({ type: 'FETCH_LOGIN_FAILED' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{}}>
      {authConfig ? (
        <FusionAuthProvider {...authConfig}>
          <FusionAuthWrapper>{children}</FusionAuthWrapper>
        </FusionAuthProvider>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthContextProvider };

AuthContextProvider.propTypes = {
  children: PropTypes.node,
};
